import React, { useState } from 'react'
import { Heading, Text, PricingOptions, Button, Stack } from '@primer/react-brand'
import { ThemeProvider, BaseStyles } from '@primer/react'
import Container from './Container'
import SectionHeader from './landing/SectionHeader'
import { useCurrentOrganization } from '../../components/useCurrentOrganization'
import AdminGateModal from '../../components/AdminGateModal'

// Mounted only after a logged-in non-admin clicks Subscribe. Subscribing is
// admin-only (it's org-wide billing), so instead of letting them reach the payment
// screen and hit a raw 403, we show the same AdminGateModal the sidebar uses. The
// org is fetched here for the owner callout. AdminGateModal is an @primer/react
// component, so it's wrapped in ThemeProvider/BaseStyles exactly as the authenticated
// Sidebar wraps it; its functional color tokens are loaded by the civic_lens_public
// pack (see that file's note).
const PricingAdminGate = ({ onClose }) => {
  const { org } = useCurrentOrganization()

  return (
    <ThemeProvider colorMode="light">
      <BaseStyles>
        <AdminGateModal isOpen onClose={onClose} org={org || {}} />
      </BaseStyles>
    </ThemeProvider>
  )
}

// Where a paying org is sent to change plans until in-app plan change ships. The
// same address the approval modal's footer gives for subscription questions.
const CHANGE_PLAN_MAILTO = 'mailto:support@civiclens.dev'

const PricingSection = ({ membershipPath = '/membership', signUpPath = '/sign-up', currentUser, variant = 'membership', currentPlan = null }) => {
  const isAdmin = Boolean(currentUser?.isAdmin)
  const [showAdminGate, setShowAdminGate] = useState(false)
  // On the home page the pricing section leads with the 7-day free-trial pitch:
  // Advanced becomes the trial plan whose CTA enters the same sign-up/trial flow as
  // "Get started", while Basic/Standard keep the normal subscription flow and
  // Pro stays "Get in touch". On /membership (the default, post-trial destination)
  // there is no trial messaging and every paid plan keeps "Subscribe".
  const isHome = variant === 'home'

  // Selecting a paid plan enters the subscription flow (payment -> apply), mirroring the
  // /membership page. A logged-out visitor is routed through sign-in with a redirect
  // back to the payment screen. A logged-in non-admin gets the admin-gate modal (same
  // as the sidebar Subscribe button) rather than being sent to payment, where the
  // admin-only server guard would otherwise reject them with a raw 403. An admin
  // proceeds to payment; the unconfirmed-email case is handled server-side from there
  // (subscribe_payment redirects to /subscribe/confirm-email).
  const goToSubscribe = (planKey) => {
    const dest = `/subscribe/payment?plan=${planKey}`
    if (!currentUser) {
      window.location.href = `/sign-in?redirect_to=${encodeURIComponent(dest)}`
      return
    }
    if (!isAdmin) {
      setShowAdminGate(true)
      return
    }
    window.location.href = dest
  }

  // The action under a paid plan's card. For a paying org (currentPlan set, only
  // on /membership) the org's own plan reads "Current plan" and cannot be clicked,
  // and every other plan points at support — the payment screen would only bounce
  // a paying org back into the app (decided 2026-09-17; plan change with proration
  // is a later slice). Otherwise it is the ordinary Subscribe entry.
  const subscribeAction = (planKey) => {
    if (currentPlan && !isHome) {
      if (planKey === currentPlan) {
        return (
          <PricingOptions.PrimaryAction as="button" disabled aria-disabled="true">
            Current plan
          </PricingOptions.PrimaryAction>
        )
      }
      return (
        // "Contact Sales", matching Pro's button word for word (Mischa,
        // 2026-09-17). "Contact us to change plans" wrapped onto two lines in the
        // narrow plan card and said the same thing in a different voice than the
        // button sitting next to it.
        <PricingOptions.SecondaryAction as="a" href={CHANGE_PLAN_MAILTO}>
          Contact Sales
        </PricingOptions.SecondaryAction>
      )
    }
    return (
      <PricingOptions.PrimaryAction
        href="#"
        onClick={(e) => {
          e.preventDefault();
          goToSubscribe(planKey);
        }}
      >
        Subscribe
      </PricingOptions.PrimaryAction>
    )
  }

  return (
    <section id="pricing" style={{ padding: isHome ? '96px 0' : '64px 0' }}>
      <Container>
        {/* The home page opens every section the same way (SectionHeader); the
            /membership page keeps its own centered intro, unchanged. */}
        {isHome ? (
          <SectionHeader
            eyebrow="Pricing · 7-day free trial"
            heading="Start with Advanced, free for a week. Then choose the plan that fits."
            lede="Membership is on a sliding scale so small teams can afford it and larger institutions carry more of the cost. Nothing is charged until the trial ends."
          />
        ) : (
          <Stack direction="vertical" alignItems="center" gap="spacious" padding="none">
            <Heading size="2" align="center">Choose your plan</Heading>
            <Text size="300" variant="muted" align="center" style={{ maxWidth: '600px' }}>
              Membership is on a sliding scale so small teams can afford it and larger institutions carry more of the cost.
            </Text>
          </Stack>
        )}

        <div style={{ marginTop: isHome ? '56px' : '48px' }}>
          <PricingOptions>
            <PricingOptions.Item>
              <PricingOptions.Heading>Basic</PricingOptions.Heading>
              {/* Annual pricing hidden 2026-09-17 — there is no annual option yet. Was: trailingText="per month / $1,000 per year" */}
              <PricingOptions.Price currencySymbol="$" trailingText="per month">100</PricingOptions.Price>
              <PricingOptions.Description>For small newsrooms and research teams</PricingOptions.Description>
              <PricingOptions.FeatureList>
                <PricingOptions.FeatureListItem variant="included">Full monitoring access</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Unlimited searches with filters</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Up to 5 team members</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">1 hour onboarding</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="excluded">No channel tracking credits</PricingOptions.FeatureListItem>
              </PricingOptions.FeatureList>
              {subscribeAction('basic')}
            </PricingOptions.Item>
            
            <PricingOptions.Item>
              <PricingOptions.Heading>Standard</PricingOptions.Heading>
              {/* Annual pricing hidden 2026-09-17 — there is no annual option yet. Was: trailingText="per month / $5,000 per year" */}
              <PricingOptions.Price currencySymbol="$" trailingText="per month">500</PricingOptions.Price>
              <PricingOptions.Description>For growing organizations and research institutions</PricingOptions.Description>
              <PricingOptions.FeatureList>
                <PricingOptions.FeatureListItem variant="included">Everything in Basic, plus:</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">500 channel tracking credits/year</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Access to 5 Public Actor Sets</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">25 private Lists</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Unlimited SMS-based tiplines</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">50 CSV exports per month</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Up to 10 team members</PricingOptions.FeatureListItem>
              </PricingOptions.FeatureList>
              {subscribeAction('standard')}
            </PricingOptions.Item>
            
            <PricingOptions.Item>
              <PricingOptions.Heading>Advanced</PricingOptions.Heading>
              {/* Annual pricing hidden 2026-09-17 — there is no annual option yet. Was: trailingText="per month / $10,000 per year" */}
              <PricingOptions.Price currencySymbol="$" trailingText="per month">1,000</PricingOptions.Price>
              <PricingOptions.Description>For large newsrooms and advanced research teams</PricingOptions.Description>
              <PricingOptions.FeatureList>
                <PricingOptions.FeatureListItem variant="included">Everything in Standard, plus:</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">1,000 channel tracking credits/year</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">API rate limit of 500 requests/hour</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Access to 10 Public Actor Sets</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">10 Projects with content labeling</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Create up to 5 Public Dashboards</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Up to 50 private Lists</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">500 CSV exports per month</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Up to 25 team members</PricingOptions.FeatureListItem>
              </PricingOptions.FeatureList>
              {isHome ? (
                // Advanced is the 7-day free-trial plan on the home page: its CTA enters
                // the same sign-up/trial flow as "Get Started for Free" rather than payment,
                // and gets the design's blue trial-button treatment (matches #0969da).
                <PricingOptions.PrimaryAction
                  as="a"
                  href={signUpPath}
                  style={{
                    backgroundColor: 'var(--base-color-scale-blue-5)',
                    borderColor: 'var(--base-color-scale-blue-5)',
                  }}
                >
                  Start free trial
                </PricingOptions.PrimaryAction>
              ) : (
                subscribeAction('advanced')
              )}
            </PricingOptions.Item>
            
            <PricingOptions.Item>
              <PricingOptions.Heading>Pro</PricingOptions.Heading>
              {/* Annual pricing hidden 2026-09-17 — there is no annual option yet. Was: trailingText="per month / $25,000 per year" */}
              <PricingOptions.Price currencySymbol="$" trailingText="per month">2,500</PricingOptions.Price>
              <PricingOptions.Description>For enterprise organizations and data-driven institutions</PricingOptions.Description>
              <PricingOptions.FeatureList>
                <PricingOptions.FeatureListItem variant="included">Everything in Advanced, plus:</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">10,000 channel tracking credits/year</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">API rate limit of 5,000 requests/hour</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Private Actor Sets and unlimited Public Actor Sets</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Unlimited Projects and Pipelines</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Unlimited Public and Private Dashboards</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Up to 100 private Lists</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Unlimited CSV exports</PricingOptions.FeatureListItem>
                <PricingOptions.FeatureListItem variant="included">Up to 50 team members</PricingOptions.FeatureListItem>
              </PricingOptions.FeatureList>
              {currentPlan === 'pro' && !isHome ? (
                <PricingOptions.PrimaryAction as="button" disabled aria-disabled="true">
                  Current plan
                </PricingOptions.PrimaryAction>
              ) : (
                // Pro is arranged directly rather than through the self-serve payment
                // flow. "Contact Sales" matches the wording the other plans' buttons
                // use for the same request (Mischa, 2026-09-17); the mailto is what
                // makes it actually reach someone.
                <PricingOptions.SecondaryAction href="mailto:support@civiclens.dev?subject=Civic%20Lens%20Pro%20membership">
                  Contact Sales
                </PricingOptions.SecondaryAction>
              )}
            </PricingOptions.Item>
          </PricingOptions>
        </div>
      </Container>

      {showAdminGate && (
        <PricingAdminGate onClose={() => setShowAdminGate(false)} />
      )}
    </section>
  )
}

export default PricingSection
